import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getFirestore, doc, getDoc, collection, query, where, getDocs, setDoc, deleteDoc, updateDoc, increment } from 'firebase/firestore';
import { app } from '../firebase-config';
import NewsCard from '../components/newsCard';
import AdCard from '../components/AdCard';
import FollowersModal from '../components/FollowersModal';
import './UserProfile.css';

const UserProfile = ({ setActiveCommentPostId }) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { currentUser, firebaseUser } = useAuth();
  
  const [profile, setProfile] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState('news');
  const [loading, setLoading] = useState(true);

  // Lists of posts
  const [userPosts, setUserPosts] = useState([]);
  const [userJobs, setUserJobs] = useState([]);
  const [userAds, setUserAds] = useState([]);

  // Modal States
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [followsMe, setFollowsMe] = useState(false);
  const [modalType, setModalType] = useState('followers');
  const [requestStatus, setRequestStatus] = useState('none'); 

  const db = getFirestore(app);

  useEffect(() => {
    const fetchProfileAndContent = async () => {
      setLoading(true);
      try {
        // 1. Fetch User Info
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          setProfile(userDoc.data());
        } else {
          setProfile({ name: 'User' });
        }

        // 2. Check if currently logged-in user is following this user
        if (currentUser) {
          const followDoc = await getDoc(doc(db, 'users', currentUser.uid, 'following', userId));
          setIsFollowing(followDoc.exists());
        }

        // 1. Fetch Approved News and sort in-memory
        const newsQ = query(
          collection(db, 'userNews'),
          where('userId', '==', userId),
          where('status', '==', 'approved')
        );
        const newsSnap = await getDocs(newsQ);
        const newsList = newsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        newsList.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
        setUserPosts(newsList);

        // 2. Fetch Jobs and sort in-memory
        const jobsQ = query(collection(db, 'jobs'), where('userId', '==', userId));
        const jobsSnap = await getDocs(jobsQ);
        const jobsList = jobsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        jobsList.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
        setUserJobs(jobsList);

        // 3. Fetch Advertisements and sort in-memory
        const adsQ = query(
          collection(db, 'advertisements'),
          where('userId', '==', userId),
          where('isActive', '==', true)
        );
        const adsSnap = await getDocs(adsQ);
        const adsList = adsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        adsList.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setUserAds(adsList);

      } catch (err) {
        console.error("Error loading user profile", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileAndContent();
  }, [userId, currentUser]);

  useEffect(() => {
  const checkFollowStates = async () => {
    if (!currentUser) return;
    try {
      // 1. Check if you follow them
      const followingDoc = await getDoc(doc(db, 'users', currentUser.uid, 'following', userId));
      if (followingDoc.exists()) {
        setRequestStatus('following');
      } else {
        // 2. Check if a request is pending
        const reqDoc = await getDoc(doc(db, 'users', userId, 'followRequests', currentUser.uid));
        if (reqDoc.exists()) {
          setRequestStatus('requested');
        } else {
          setRequestStatus('none');
        }
      }

      // 3. Check if they follow you (to enable "Follow Back" text)
      const followerDoc = await getDoc(doc(db, 'users', currentUser.uid, 'followers', userId));
      setFollowsMe(followerDoc.exists());
    } catch (err) {
      console.error("Error checking follow states:", err);
    }
  };

  checkFollowStates();
}, [userId, currentUser]);
  const getFormattedLocation = (profileObj) => {
  if (!profileObj) return "Location not specified";
  const loc = profileObj.location;
  if (!loc) return profileObj.detectedLocation || "Location not specified";
  
  // Safely filters out missing fields or literal "undefined" string values
  const parts = [
    loc.mandal || loc.city,
    loc.constituency,
    loc.district,
    loc.state
  ].filter(p => p && p !== "undefined" && String(p).trim() !== "");
  
  return parts.length > 0 ? parts.join(', ') : "Location not specified";
};

  // 3. Update the handleFollowToggle to invoke Express API:
  // Updated handleFollowToggle to handle 'requested' state properly
  const handleFollowToggle = async () => {
    if (!currentUser) {
      alert("Please log in to perform this action.");
      return;
    }

    if (requestStatus === 'requested') {
      alert("Follow request is already pending.");
      return;
    }

    try {
      const idToken = await firebaseUser.getIdToken(true);
      
      if (requestStatus === 'following') {
        // Unfollow path
        await axios.post('https://api-5iacy4tfya-uc.a.run.app/unfollow', 
          { targetUserId: userId }, 
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        setRequestStatus('none');
        setProfile(prev => ({ ...prev, followersCount: Math.max(0, (prev.followersCount || 1) - 1) }));
      } else if (requestStatus === 'none') {
        // Send Follow Request path
        const res = await axios.post('https://api-5iacy4tfya-uc.a.run.app/follow-request', 
          { targetUserId: userId }, 
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        setRequestStatus('requested');
      }
    } catch (error) {
      console.error("Follow operation failed:", error);
      const errorMsg = error.response?.data?.message || "";
      // If request is already pending on server, automatically set status to 'requested'
      if (errorMsg.includes("already pending")) {
        setRequestStatus('requested');
      }
      alert(errorMsg || "Action failed.");
    }
  };

  if (loading) {
    return <div className="user-profile-container loading"><h3>Loading Profile...</h3></div>;
  }

  return (
    <div className="user-profile-container">
      <div className="user-profile-header-card">
        <button onClick={() => navigate(-1)} className="back-nav-btn">← Back</button>
        
        <div className="user-profile-avatar">
          {profile?.name?.charAt(0).toUpperCase()}
        </div>
        
        <h2 className="user-profile-title-name">{profile?.name || 'Anonymous User'}</h2>
        {/* Display cleaned location on the profile */}
        <p className="user-profile-location" style={{ fontSize: '13px', color: '#666', margin: '-10px 0 15px 0' }}>
          📍 {getFormattedLocation(profile)}
        </p>
        <div className="profile-social-stats">
          <div className="stat-box" onClick={() => { setModalType('followers'); setShowFollowersModal(true); }}>
            <span className="stat-count">{profile?.followersCount || 0}</span>
            <span className="stat-label">Followers</span>
          </div>
          <div className="stat-box" onClick={() => { setModalType('following'); setShowFollowersModal(true); }}>
            <span className="stat-count">{profile?.followingCount || 0}</span>
            <span className="stat-label">Following</span>
          </div>
        </div>

        {currentUser?.uid !== userId && (
          <button 
            className={`follow-action-btn ${requestStatus === 'following' ? 'following' : requestStatus === 'requested' ? 'requested' : ''}`} 
            onClick={handleFollowToggle}
            disabled={requestStatus === 'requested'}
          >
            {requestStatus === 'following' 
              ? 'Following' 
              : requestStatus === 'requested' 
              ? 'Requested' 
              : followsMe 
              ? 'Follow Back' 
              : 'Follow'}
          </button>
        )}
      </div>

      <div className="user-posts-container">
        <div className="tabs-header">
          <button 
            className={`tab-button ${activeTab === 'news' ? 'active' : ''}`}
            onClick={() => setActiveTab('news')}
          >
            News ({userPosts.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'jobs' ? 'active' : ''}`}
            onClick={() => setActiveTab('jobs')}
          >
            Jobs ({userJobs.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'ads' ? 'active' : ''}`}
            onClick={() => setActiveTab('ads')}
          >
            Ads ({userAds.length})
          </button>
        </div>

        <div className="tabs-content">
          {activeTab === 'news' && (
            <div className="public-feed-stage">
              {userPosts.length === 0 ? (
                <p className="no-posts-hint">No news posts yet.</p>
              ) : (
                userPosts.map(item => (
                  <NewsCard
                    key={item.id}
                    newsItem={item}
                    isFeedView={false}
                    setActiveCommentPostId={setActiveCommentPostId}
                    isActive={false}
                  />
                ))
              )}
            </div>
          )}

          {activeTab === 'jobs' && (
            <div className="public-jobs-grid">
              {userJobs.length === 0 ? (
                <p className="no-posts-hint">No jobs listed.</p>
              ) : (
                userJobs.map(job => (
                  <div key={job.id} className="post-card job-card">
                    <h3>{job.title}</h3>
                    <p><strong>Company:</strong> {job.companyName || 'Government'}</p>
                    <p><strong>Location:</strong> {job.locationState}</p>
                    <p className="post-content-snippet">{job.description?.substring(0, 100)}...</p>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'ads' && (
            <div className="public-ads-list">
              {userAds.length === 0 ? (
                <p className="no-posts-hint">No active advertisements.</p>
              ) : (
                userAds.map(ad => (
                  <AdCard key={ad.id} adData={ad} isActive={true} />
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {showFollowersModal && (
        <FollowersModal
          userId={userId}
          type={modalType}
          onClose={() => setShowFollowersModal(false)}
        />
      )}
    </div>
  );
};

export default UserProfile;